// src/pages/events/components/rsvp-sheet.tsx
import { useMemo, useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  Check,
  ChevronRight,
  Copy,
  CreditCard,
  Download,
  Edit2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  RefreshCw,
  Search,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { PermissionGuard } from "@/components/guards/PermissionGuard";
import { useEventRSVPs, useEvents } from "@/lib/api/hooks";
import type { Event, RSVPEntry } from "@/lib/api/types";
import { downloadCsv } from "@/lib/utils/csv";
import { formatKenyanPhone, isEventPast, parseEventDate } from "@/lib/utils/events";
import { RSVPForm, type RSVPFormValues } from "./rsvp-form";

type PaymentState = "free" | "paid" | "manual" | "pending" | "expired";
type PaymentFilter = "all" | "paid" | "pending" | "expired";

function paymentState(rsvp: RSVPEntry): PaymentState {
  switch (rsvp.paymentStatus) {
    case "paid":
      return "paid";
    case "manual":
      return "manual";
    case "pending": {
      const expires = toDate(rsvp.paymentExpiresAt);
      return expires && expires.getTime() > Date.now() ? "pending" : "expired";
    }
    default:
      return "free";
  }
}

const paymentBadge: Record<PaymentState, { label: string; className: string } | null> = {
  free: null,
  paid: { label: "Paid", className: "border-green-600 text-green-700 dark:text-green-400" },
  manual: { label: "Confirmed by admin", className: "border-blue-600 text-blue-700 dark:text-blue-400" },
  pending: { label: "Awaiting payment", className: "border-orange-500 text-orange-600" },
  expired: { label: "Payment not completed", className: "border-red-600 text-red-600" },
};

function PaymentBadge({ rsvp }: { rsvp: RSVPEntry }) {
  const badge = paymentBadge[paymentState(rsvp)];
  if (!badge) return null;
  return (
    <Badge variant="outline" className={`shrink-0 text-[11px] font-medium ${badge.className}`}>
      {badge.label}
    </Badge>
  );
}

const formatKes = (amount: number) => `KES ${amount.toLocaleString("en-KE")}`;

type View = { mode: "list" } | { mode: "detail"; rsvp: RSVPEntry } | { mode: "form"; rsvp: RSVPEntry | null };

interface RSVPSheetProps {
  event: Event | null;
  onClose: () => void;
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function toDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === "") return null;
  // D1 timestamps may arrive as seconds, milliseconds or ISO strings
  const date =
    typeof value === "number" ? new Date(value < 1e12 ? value * 1000 : value) : new Date(value as string);
  return Number.isNaN(date.getTime()) ? null : date;
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-muted-foreground"
      aria-label={`Copy ${label}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error("Couldn't copy to clipboard");
        }
      }}
    >
      {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
}

function ListSkeleton() {
  return (
    <div className="divide-y divide-border" aria-label="Loading attendees" aria-busy="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-6 py-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-56" />
          </div>
          <Skeleton className="h-3 w-16" />
        </div>
      ))}
    </div>
  );
}

function ContactRow({
  icon: Icon,
  label,
  value,
  display,
  href,
  actionLabel,
}: {
  icon: typeof Mail;
  label: string;
  value: string;
  display?: string;
  href: string;
  actionLabel: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border p-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="truncate text-sm font-medium text-foreground">{display ?? value}</p>
      </div>
      <CopyButton value={value} label={label} />
      <Button variant="outline" size="sm" asChild>
        <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
          {actionLabel}
        </a>
      </Button>
    </div>
  );
}

export function RSVPSheet({ event, onClose }: RSVPSheetProps) {
  const eventId = event?.id ?? null;
  const { data: rsvps = [], isLoading, isError, refetch, isFetching } = useEventRSVPs(eventId);
  const { createRsvp, updateRsvp, deleteRsvp } = useEvents({ enabled: false });

  const [view, setView] = useState<View>({ mode: "list" });
  const [search, setSearch] = useState("");
  const [paymentFilter, setPaymentFilter] = useState<PaymentFilter>("all");
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<RSVPEntry | null>(null);

  const sorted = useMemo(
    () =>
      [...rsvps].sort((a, b) => (toDate(b.createdAt)?.getTime() ?? 0) - (toDate(a.createdAt)?.getTime() ?? 0)),
    [rsvps]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const byPayment = paymentFilter === "all" ? sorted : sorted.filter((r) => {
      const state = paymentState(r);
      return paymentFilter === "paid" ? state === "paid" || state === "manual" : state === paymentFilter;
    });
    if (!q) return byPayment;
    return byPayment.filter(
      (r) =>
        r.fullName.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.mpesaPhone.includes(q.replace(/\D/g, "") || q) ||
        r.whatsappPhone.includes(q.replace(/\D/g, "") || q)
    );
  }, [sorted, search, paymentFilter]);

  if (!event) return null;

  const capacity = event.seatsAvailable;
  const isPaidEvent = event.fee > 0;
  // Seats taken comes from the server: confirmed RSVPs plus active payment holds
  const registered = event.rsvpCount ?? 0;
  const counts = rsvps.reduce(
    (acc, r) => {
      const state = paymentState(r);
      if (state === "paid" || state === "manual") acc.paid += 1;
      else if (state === "pending") acc.pending += 1;
      else if (state === "expired") acc.expired += 1;
      return acc;
    },
    { paid: 0, pending: 0, expired: 0 }
  );
  const seatsLeft = Math.max(capacity - registered, 0);
  const fillPercent = capacity > 0 ? Math.min((registered / capacity) * 100, 100) : 0;
  const past = isEventPast(event.date);
  const isFull = seatsLeft === 0;

  const handleClose = () => {
    setView({ mode: "list" });
    setSearch("");
    setPaymentFilter("all");
    onClose();
  };

  // For attendees who paid in cash or by direct M-Pesa instead of finishing online checkout
  const handleConfirmManually = async (rsvp: RSVPEntry) => {
    setConfirmingId(rsvp.id);
    try {
      await createRsvp({
        eventId: event.id,
        data: {
          fullName: rsvp.fullName,
          email: rsvp.email,
          mpesaPhone: rsvp.mpesaPhone,
          whatsappPhone: rsvp.whatsappPhone,
        } as any,
      });
      setView({ mode: "detail", rsvp: { ...rsvp, paymentStatus: "manual", paymentExpiresAt: null } });
    } catch {
      // Error toast is shown by the API client
    } finally {
      setConfirmingId(null);
    }
  };

  const handleSubmit = async (values: RSVPFormValues) => {
    const current = view.mode === "form" ? view.rsvp : null;
    if (current) {
      await updateRsvp({ eventId: event.id, rsvpId: current.id, data: values as any });
      setView({ mode: "detail", rsvp: { ...current, ...values } });
    } else {
      await createRsvp({ eventId: event.id, data: values as any });
      setView({ mode: "list" });
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteRsvp({ eventId: event.id, rsvpId: pendingDelete.id });
      setView({ mode: "list" });
    } finally {
      setPendingDelete(null);
    }
  };

  const handleExport = () => {
    downloadCsv(
      `${event.title.replace(/[^\w-]+/g, "_")}_attendees_${format(new Date(), "yyyy-MM-dd")}.csv`,
      ["Full Name", "Email", "M-Pesa Phone", "WhatsApp", "Registered", "Payment", "Amount (KES)", "Paystack Reference"],
      sorted.map((r) => [
        r.fullName,
        r.email,
        r.mpesaPhone,
        r.whatsappPhone,
        toDate(r.createdAt) ? format(toDate(r.createdAt)!, "yyyy-MM-dd HH:mm") : "",
        paymentBadge[paymentState(r)]?.label ?? "Free",
        r.amountDue ? r.amountDue / 100 : 0,
        r.paymentReference ?? "",
      ])
    );
  };

  return (
    <Sheet open={!!event} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent side="right" className="flex flex-col gap-0 p-0 sm:max-w-xl lg:max-w-2xl">
        {/* Event summary */}
        <SheetHeader className="space-y-3 border-b border-border p-6 pr-12 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <SheetTitle className="text-xl">Attendees</SheetTitle>
            {past && <Badge variant="secondary">Past event</Badge>}
            {!past && isFull && (
              <Badge variant="outline" className="border-red-600 text-red-600">
                Fully booked
              </Badge>
            )}
          </div>
          <SheetDescription asChild>
            <div className="space-y-1">
              <p className="font-medium text-foreground">{event.title}</p>
              <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {format(parseEventDate(event.date), "EEE, MMM d, yyyy")}
                </span>
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {event.location}
                </span>
                <span className="inline-flex items-center gap-1">
                  <CreditCard className="h-3 w-3" />
                  {isPaidEvent ? `${formatKes(event.fee)} per person (Paystack)` : "Free"}
                </span>
              </p>
            </div>
          </SheetDescription>

          <div className={`grid gap-3 pt-1 ${isPaidEvent ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}>
            {[
              { label: "Seats taken", value: registered },
              { label: "Capacity", value: capacity },
              { label: "Seats left", value: seatsLeft },
              ...(isPaidEvent ? [{ label: "Collected", value: formatKes(event.amountCollected ?? 0) }] : []),
            ].map((stat) => (
              <div key={stat.label} className="rounded-lg border border-border bg-muted/40 px-3 py-2">
                <p className="text-xs text-muted-foreground">{stat.label}</p>
                <p className="text-lg font-semibold tabular-nums text-foreground">{stat.value}</p>
              </div>
            ))}
          </div>
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuenow={Math.round(fillPercent)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Seats filled"
          >
            <div
              className={`h-full rounded-full transition-all ${
                fillPercent >= 100 ? "bg-red-500" : fillPercent >= 80 ? "bg-orange-500" : "bg-primary"
              }`}
              style={{ width: `${fillPercent}%` }}
            />
          </div>
        </SheetHeader>

        {/* Body */}
        {view.mode === "form" ? (
          <div className="flex-1 overflow-y-auto p-6">
            <button
              type="button"
              onClick={() => setView(view.rsvp ? { mode: "detail", rsvp: view.rsvp } : { mode: "list" })}
              className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <h3 className="mb-4 text-base font-semibold text-foreground">
              {view.rsvp ? "Edit attendee" : "Add attendee"}
            </h3>
            <RSVPForm
              initialData={view.rsvp}
              onSubmit={handleSubmit}
              onCancel={() => setView(view.rsvp ? { mode: "detail", rsvp: view.rsvp } : { mode: "list" })}
            />
          </div>
        ) : view.mode === "detail" ? (
          <div className="flex-1 overflow-y-auto p-6">
            <button
              type="button"
              onClick={() => setView({ mode: "list" })}
              className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> All attendees
            </button>

            <div className="mb-6 flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
                {initials(view.rsvp.fullName)}
              </div>
              <div className="min-w-0">
                <h3 className="truncate text-lg font-semibold text-foreground">{view.rsvp.fullName}</h3>
                {toDate(view.rsvp.createdAt) && (
                  <p className="text-sm text-muted-foreground">
                    Registered {format(toDate(view.rsvp.createdAt)!, "MMM d, yyyy 'at' HH:mm")}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <ContactRow
                icon={Mail}
                label="Email"
                value={view.rsvp.email}
                href={`mailto:${view.rsvp.email}`}
                actionLabel="Email"
              />
              <ContactRow
                icon={Phone}
                label="M-Pesa number"
                value={view.rsvp.mpesaPhone}
                display={formatKenyanPhone(view.rsvp.mpesaPhone)}
                href={`tel:+${view.rsvp.mpesaPhone}`}
                actionLabel="Call"
              />
              <ContactRow
                icon={MessageCircle}
                label="WhatsApp number"
                value={view.rsvp.whatsappPhone}
                display={formatKenyanPhone(view.rsvp.whatsappPhone)}
                href={`https://wa.me/${view.rsvp.whatsappPhone}`}
                actionLabel="WhatsApp"
              />
            </div>

            {isPaidEvent && (
              <div className="mt-6 rounded-lg border border-border p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-foreground">Payment</h4>
                  <PaymentBadge rsvp={view.rsvp} />
                </div>
                <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Amount</dt>
                  <dd className="text-foreground">{formatKes((view.rsvp.amountDue ?? event.fee * 100) / 100)}</dd>
                  {view.rsvp.paymentReference && (
                    <>
                      <dt className="text-muted-foreground">Reference</dt>
                      <dd className="flex items-center gap-1 font-mono text-xs text-foreground">
                        <span className="truncate">{view.rsvp.paymentReference}</span>
                        <CopyButton value={view.rsvp.paymentReference} label="reference" />
                      </dd>
                    </>
                  )}
                  {toDate(view.rsvp.paidAt) && (
                    <>
                      <dt className="text-muted-foreground">Paid</dt>
                      <dd className="text-foreground">{format(toDate(view.rsvp.paidAt)!, "MMM d, yyyy 'at' HH:mm")}</dd>
                    </>
                  )}
                  {paymentState(view.rsvp) === "pending" && toDate(view.rsvp.paymentExpiresAt) && (
                    <>
                      <dt className="text-muted-foreground">Seat held until</dt>
                      <dd className="text-foreground">{format(toDate(view.rsvp.paymentExpiresAt)!, "HH:mm")}</dd>
                    </>
                  )}
                </dl>
                {(paymentState(view.rsvp) === "pending" || paymentState(view.rsvp) === "expired") && (
                  <PermissionGuard resource="events" action="update">
                    <div className="mt-4 border-t border-border pt-4">
                      <p className="mb-3 text-xs text-muted-foreground">
                        {paymentState(view.rsvp) === "expired"
                          ? "This person started checkout but didn't pay, so their seat was released."
                          : "Waiting for Paystack to confirm the payment."}{" "}
                        If they paid you another way (cash or direct M-Pesa), you can confirm them here.
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={confirmingId === view.rsvp.id}
                        onClick={() => handleConfirmManually(view.rsvp)}
                      >
                        <Check className="mr-2 h-4 w-4" />
                        {confirmingId === view.rsvp.id ? "Confirming..." : "Confirm attendance manually"}
                      </Button>
                    </div>
                  </PermissionGuard>
                )}
              </div>
            )}

            <PermissionGuard resource="events" action="update">
              <div className="mt-8 flex flex-wrap gap-2 border-t border-border pt-6">
                {!past && (
                  <Button variant="outline" onClick={() => setView({ mode: "form", rsvp: view.rsvp })}>
                    <Edit2 className="mr-2 h-4 w-4" /> Edit details
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => setPendingDelete(view.rsvp)}
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Remove attendee
                </Button>
              </div>
            </PermissionGuard>
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 border-b border-border px-6 py-3">
              <div className="relative min-w-[180px] flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, email or phone"
                  className="pl-9"
                  aria-label="Search attendees"
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => refetch()}
                disabled={isFetching}
                aria-label="Refresh attendees"
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              </Button>
              <Button variant="outline" onClick={handleExport} disabled={isLoading || rsvps.length === 0}>
                <Download className="mr-2 h-4 w-4" /> Export
              </Button>
              <PermissionGuard resource="events" action="update">
                <Button
                  onClick={() => setView({ mode: "form", rsvp: null })}
                  disabled={isFull}
                  title={isFull ? "This event is fully booked" : undefined}
                >
                  <UserPlus className="mr-2 h-4 w-4" /> Add
                </Button>
              </PermissionGuard>
            </div>

            {isPaidEvent && !isLoading && rsvps.length > 0 && (
              <div className="flex flex-wrap gap-2 border-b border-border px-6 py-2" role="tablist" aria-label="Filter by payment">
                {(
                  [
                    { value: "all", label: "All", count: rsvps.length },
                    { value: "paid", label: "Paid", count: counts.paid },
                    { value: "pending", label: "Awaiting payment", count: counts.pending },
                    { value: "expired", label: "Not completed", count: counts.expired },
                  ] as { value: PaymentFilter; label: string; count: number }[]
                ).map((chip) => (
                  <button
                    key={chip.value}
                    type="button"
                    role="tab"
                    aria-selected={paymentFilter === chip.value}
                    onClick={() => setPaymentFilter(chip.value)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      paymentFilter === chip.value
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {chip.label} <span className="tabular-nums opacity-80">{chip.count}</span>
                  </button>
                ))}
              </div>
            )}

            {/* List */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <ListSkeleton />
              ) : isError ? (
                <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                  <AlertCircle className="h-10 w-10 text-destructive" />
                  <p className="font-medium text-foreground">Couldn't load attendees</p>
                  <p className="text-sm text-muted-foreground">Check your connection and try again.</p>
                  <Button variant="outline" onClick={() => refetch()}>
                    <RefreshCw className="mr-2 h-4 w-4" /> Retry
                  </Button>
                </div>
              ) : rsvps.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
                  <div className="mb-2 rounded-full bg-muted p-4">
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="font-medium text-foreground">No RSVPs yet</p>
                  <p className="max-w-xs text-sm text-muted-foreground">
                    People who register on the website will appear here.
                  </p>
                </div>
              ) : filtered.length === 0 ? (
                <p className="px-6 py-16 text-center text-sm text-muted-foreground">
                  {search ? `No attendees match "${search}".` : "No attendees in this group."}
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {filtered.map((rsvp) => {
                    const registeredAt = toDate(rsvp.createdAt);
                    return (
                      <li key={rsvp.id}>
                        <button
                          type="button"
                          onClick={() => setView({ mode: "detail", rsvp })}
                          className="flex w-full items-center gap-3 px-6 py-4 text-left transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                        >
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                            {initials(rsvp.fullName)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="truncate text-sm font-medium text-foreground">{rsvp.fullName}</p>
                              {isPaidEvent && <PaymentBadge rsvp={rsvp} />}
                            </div>
                            <p className="truncate text-xs text-muted-foreground">
                              {rsvp.email} · {formatKenyanPhone(rsvp.whatsappPhone)}
                            </p>
                          </div>
                          {registeredAt && (
                            <span className="hidden shrink-0 text-xs text-muted-foreground sm:block">
                              {formatDistanceToNow(registeredAt, { addSuffix: true })}
                            </span>
                          )}
                          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {!isLoading && rsvps.length > 0 && (
              <div className="border-t border-border px-6 py-3 text-xs text-muted-foreground">
                Showing {filtered.length} of {rsvps.length} attendee{rsvps.length === 1 ? "" : "s"}
              </div>
            )}
          </>
        )}

        <ConfirmationDialog
          isOpen={!!pendingDelete}
          onClose={() => setPendingDelete(null)}
          onConfirm={handleDelete}
          title="Remove attendee"
          description={`Remove ${pendingDelete?.fullName ?? "this attendee"} from "${event.title}"? This frees up their seat and can't be undone.`}
        />
      </SheetContent>
    </Sheet>
  );
}
